import isEqual from 'lodash/isEqual';
import {useCallback, useEffect, useState} from 'react';
import {shallowEqual, useSelector} from 'react-redux';
import {ConfirmNotifyModal} from '~/SignUpSheets/Create/routes/Customize/components/Builder/Elements/ConfirmNotifyModal';
import {NavBottom} from '~/SignUpSheets/components/NavBottom/NavBottom';
import {useSheetActions} from '~/SignUpSheets/hooks/useSheetActions';
import {useUpdateSheet} from '~/SignUpSheets/hooks/useUpdateSheet';
import {useValidation} from '~/SignUpSheets/hooks/useValidation';
import {
  selectGateUnlockedForSheet,
  selectIsInEditMode,
  selectIsPreviewMode,
  selectLocalWishlists,
  selectSheetId,
  selectWishlistOption,
  selectWishlists,
} from '~/SignUpSheets/reducers/sheet/selectors';
import {VolunteerView} from '~/SignUpSheets/View/routes/VolunteerView/VolunteerView';
import {useIsLargeAndUp} from '~library/hooks/useMatchQuery';
import {CustomizeView} from './CustomizeView';

export const Customize = ({trackChangeUpstream}) => {
  const {
    onSave,
    autoSave,
    isSavingSheet,
    isPublishingSheet,
    isPublishSuccess,
    isSaveSuccess,
    isSaveError,
  } = useSheetActions();
  const sheetId = useSelector(selectSheetId);
  const {updateSheetDetails} = useUpdateSheet();
  const isInEditMode = useSelector(selectIsInEditMode);
  const localWishlists = useSelector(selectLocalWishlists, shallowEqual);
  const wishlists = useSelector(selectWishlists, shallowEqual);
  const wishlistOption = useSelector(selectWishlistOption);
  const isPreviewMode = useSelector(selectIsPreviewMode);
  const {ValidationModal} = useValidation();
  const showNotifyVolunteersModal = useSelector(selectGateUnlockedForSheet);
  const [triggerConfirmNotifyOnSave, setTriggerConfirmNotifyOnSave] = useState(false);
  const [showConfirmNotifyModal, setShowConfirmNotifyModal] = useState(false);
  const [notifyVolunteersOfChanges, setNotifyVolunteersOfChanges] = useState(null);
  const isLarge = useIsLargeAndUp();

  useEffect(() => {
    setTriggerConfirmNotifyOnSave(showNotifyVolunteersModal);
  }, [showNotifyVolunteersModal]);

  // biome-ignore lint/correctness/useExhaustiveDependencies: existing error
  const handleSaveOrPublish = useCallback(
    () =>
      new Promise((resolve) => {
        const usingWishlists = wishlistOption === 'my-wishlist';
        const hasEmptyWishlist = localWishlists.some((w) => w.trim() === '');
        if ((usingWishlists && hasEmptyWishlist) || !isEqual(wishlists, localWishlists)) {
          updateSheetDetails({
            payload: {
              // biome-ignore lint/style/useNamingConvention: existing error
              has_unsaved_wishlists: true,
            },
          });
          resolve();
        } else {
          updateSheetDetails({
            payload: {
              // biome-ignore lint/style/useNamingConvention: existing error
              has_unsaved_wishlists: false,
            },
            errorsToClear: [['wishlists']],
          });
          if (isInEditMode) {
            onSave(false)
              .then((result) => {
                if (result) {
                  window.location.href = `/signup-sheet/${sheetId}`;
                }
              })
              .catch(evite.error)
              .finally(resolve);
          } else {
            autoSave(true).catch(evite.error).finally(resolve);
          }
        }
      }),
    [autoSave, wishlistOption, localWishlists, onSave, updateSheetDetails, wishlists, isInEditMode]
  );

  const handleNotifyModalBeforeSave = useCallback(() => {
    if (triggerConfirmNotifyOnSave) {
      setShowConfirmNotifyModal(true);
    } else {
      handleSaveOrPublish();
    }
  }, [triggerConfirmNotifyOnSave, handleSaveOrPublish]);

  const setNotifyVolunteersOfChangesHandler = useCallback(
    (value) => () => {
      setNotifyVolunteersOfChanges(value);
    },
    []
  );

  // biome-ignore lint/correctness/useExhaustiveDependencies: existing error
  useEffect(() => {
    if (notifyVolunteersOfChanges !== null) {
      updateSheetDetails({
        payload: {
          // biome-ignore lint/style/useNamingConvention: existing error
          notify_volunteers: notifyVolunteersOfChanges,
        },
      });
      handleSaveOrPublish();
    }
  }, [notifyVolunteersOfChanges]);

  return (
    <>
      {isPreviewMode
        ? <VolunteerView />
        : <CustomizeView trackChangeUpstream={trackChangeUpstream} />}
      <NavBottom
        show={!isLarge}
        // primary props
        onClickPrimary={handleNotifyModalBeforeSave}
        buttonTextPrimary={isInEditMode ? 'Save changes' : 'Publish'}
        buttonVariantPrimary="primary"
        dataQaIdPrimary={isInEditMode ? 'save-sheet-cta' : 'publish-sheet-cta'}
        disabledPrimary={!sheetId || isSavingSheet || isPublishingSheet || isPublishSuccess}
        // secondary props
        showSecondary={!isInEditMode}
        onClickSecondary={() => autoSave()}
        buttonTextSecondary="Save"
        dataQaIdSecondary="save-progress-cta"
        disabledSecondary={isSavingSheet || isPublishingSheet}
        disableProcessingSecondary
        classNameSecondary="signup-sheet__customize__bottom-cta__save-button"
        isLoadingSecondary={isSavingSheet}
        isSuccessSecondary={isSaveSuccess}
        isErrorSecondary={isSaveError}
        fullWidthSecondary={false}
      />
      {ValidationModal}
      {showConfirmNotifyModal && (
        <ConfirmNotifyModal
          onPrimaryAction={setNotifyVolunteersOfChangesHandler(true)}
          onSecondaryAction={setNotifyVolunteersOfChangesHandler(false)}
          onClose={setNotifyVolunteersOfChangesHandler(false)}
        />
      )}
    </>
  );
};
